<script setup lang="ts">
import { KsInput } from "@aschehoug/kloss";

const props = defineProps<{
  variant: SingleProductVariant | PacketVariant;
  dir: "horizontal" | "vertical";
}>();

const totStudents = useTotStudents();

const min = computed(() => (props.variant.fixedAmount ? totStudents.value : 1));

const quantity = ref(min.value);

function handleChange(event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  if (value < min.value) {
    quantity.value = min.value;
  }
}

const auth = useAuthorizeStore();

const prices = usePrices(props.variant);

const sum = computed(() => {
  if (prices.value === "NOT_APPLICABLE") {
    return {
      exVat: 0,
      incVat: 0,
    };
  }

  return {
    exVat: quantity.value * prices.value.actual.exVat,
    incVat: quantity.value * prices.value.actual.incVat,
  };
});

const hasVat = computed(() => sum.value.incVat !== sum.value.exVat);
</script>

<template>
  <div v-if="prices === 'NOT_APPLICABLE'" class="card @container">
    <CardsCompanyPriceDisclaimer class="@xs:flex-row @xs:py-l @xs:text-start" />
  </div>
  <div v-else class="card">
    <div
      v-if="!auth.isConsumer"
      class="price"
      :class="dir"
    >
      <IconsDiscount
        v-if="prices.discounted && prices.discounted.percentage < 100"
        :value="prices.discounted.percentage"
        size="small"
        class="[--background:theme(colors.coral.pop)] [--color:white] [--stroke:white]"
      />
      <div>
        <div class="price">
          <span v-if="prices.discounted && prices.discounted.percentage === 100" class="text-xl font-semibold text-coral-40">
            Gratis*
          </span>
          <template v-else>
            <del
              v-if="prices.discounted"
              v-text="norwegianPrice(prices.original.exVat)"
            />
            <strong>
              {{ norwegianPrice(prices.actual.exVat) }} per tilgang per år
            </strong>
          </template>
          <span v-if="dir === 'horizontal' && hasVat">
            ({{ norwegianPrice(prices.actual.incVat) }} inkl. mva.)
          </span>
        </div>
        <div v-if="dir === 'vertical' && hasVat">
          {{ norwegianPrice(prices.actual.incVat) }} inkl. mva.
        </div>
        <DiscountDisclaimer
          :prices="prices"
          class="text-xs"
          fallback="Tilbudet gjelder første år. Deretter gjelder ordinær pris."
        />
      </div>
    </div>

    <div
      class="form"
      :class="[dir, { consumer: auth.isConsumer }]"
    >
      <template v-if="auth.isConsumer">
        <span>
          <del
            v-if="prices?.discounted"
            v-text="norwegianPrice(prices.original.incVat)"
          />
          <strong>
            {{ norwegianPrice(prices?.actual.incVat) }} per tilgang per år
          </strong>
        </span>

        <AddToCartButton :variant="variant" :quantity="quantity" />
      </template>
      <template v-else>
        <div class="quantities">
          <label for="quantity">Tilganger</label>
          <KsInput
            v-model.number="quantity"
            class="quantity-input"
            name="quantity"
            type="number"
            :min="min"
            :disabled="variant.fixedAmount"
            @focus="$event.target.select()"
            @change="handleChange"
          />
        </div>

        <div class="sum" :class="dir">
          <label for="sum">Sum&nbsp;</label>
          <output
            id="sum"
            for="quantity"
            v-text="norwegianPrice(sum.incVat)"
          />
          <span> inkl.&nbsp;mva.</span>
        </div>

        <AddToCartButton :variant="variant" :quantity="quantity">
          Legg i handlekurv
        </AddToCartButton>
      </template>
    </div>

    <PriceLoginCta
      class="disclaimer"
      :priceable="variant"
      singular-price
      :class="{ 'text-center': dir === 'vertical' }"
    />
  </div>
</template>

<style scoped lang="scss">
.card {
  background: white;
  padding: theme("spacing.m");
  border-radius: theme("borderRadius.md");
  display: flex;
  flex-direction: column;
  gap: theme("spacing.s");
  container-type: inline-size;
}

.price {
  font-size: pixelsToRem(14);
  line-height: pixelsToRem(22);
  display: flex;
  align-items: center;
  gap: theme("spacing.s");

  &.vertical {
    flex-direction: column;
    text-align: center;

    > div {
      margin-inline: theme("spacing.l");

      > .price {
        justify-content: center;
      }
    }
  }

  > div {
    > .price {
      gap: theme("spacing.2xs");
      flex-wrap: wrap;
    }
  }
}

.disclaimer {
  font-size: pixelsToRem(12);
  line-height: pixelsToRem(16);
}

.form {
  background: #f6f9f9;
  display: flex;
  align-items: center;
  padding-block: var(--padding-block);
  padding-inline: var(--padding-inline);
  border-radius: pixelsToRem(5);
  column-gap: min(theme("spacing.s"), 5%);
  row-gap: 0.25rem;

  &.consumer {
    justify-content: space-between;
    padding: theme("spacing.m");

    > span {
      display: flex;
      gap: 0.5rem;
      font-size: pixelsToRem(14);
      line-height: pixelsToRem(22);
    }
  }

  &.vertical {
    flex-direction: column;

    > .quantities > * {
      text-align: center;
    }

    :deep(.ks-button) {
      align-self: stretch;
    }
  }

  &.horizontal {
    flex-wrap: wrap;

    :deep(.ks-button) {
      margin-left: auto;
      margin-top: 0.75rem;
    }
  }

  :deep(.ks-button) {
    color: black;
    align-self: center;
    flex: 1;
  }

  > .quantities {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    > label {
      font-size: 0.75rem;
    }

    > .quantity-input {
      width: 10ch; // Allows for 4 digits, so up to 9999 students.
      border: 1px solid #a8a8a8;
      border-radius: 0;
      --ks-input: white;
    }
  }
}

.sum {
  flex: 1 auto;
  font-weight: 600;
  font-size: 1rem;
  line-height: pixelsToRem(22);

  > span {
    font-weight: normal;
  }

  &.vertical {
    margin-top: theme("spacing.m");
    margin-bottom: theme("spacing.2xs");
  }

  &.horizontal {
    width: 35%;
    margin-top: 1.25rem;
  }
}
</style>
